import React, { useEffect, useMemo, useState } from "react";
import "./floor.css";
import { useFloorFeed } from "../tv/useFloorFeed";
import { useCommandData, equityOf } from "./useCommandData";
import { useAgentStatus, statusOf } from "./useAgentStatus";
import { deriveFlow, firstLine, maskText } from "./floorUtil";
import { FloorHeader } from "./FloorHeader";
import { PositionCards } from "./PositionCards";
import { Vault, OrderLog } from "./VaultColumn";
import { FloorSlideshow } from "./FloorSlideshow";
import { FloorTvDashboard } from "./FloorTvDashboard";

const USD_KEY = "nexus_floor_usd";

const useVault = (tokens) => {
  const [vault, setVault] = useState([]);
  useEffect(() => {
    const won = tokens.filter((t) => t.approved);
    if (!won.length) return;
    setVault((v) => {
      const fresh = won.filter((w) => !v.some((x) => x.ticker === w.ticker));
      return fresh.length ? [...fresh, ...v].slice(0, 10) : v;
    });
  }, [tokens]);
  return vault;
};

export default function FloorCommandRoom({ session, tv = false }) {
  const feed = useFloorFeed();
  const d = useCommandData(session.user_id, tv);
  const { evs, now } = useAgentStatus();
  const flow = useMemo(() => deriveFlow(feed.byAgent), [feed.byAgent]);
  const vault = useVault(flow.tokens);
  const [usdOn, setUsdOn] = useState(() => { try { return localStorage.getItem(USD_KEY) === "1"; } catch (e) { return false; } });
  const toggleUsd = () => setUsdOn((v) => { try { localStorage.setItem(USD_KEY, v ? "0" : "1"); } catch (e) { /* ignore */ } return !v; });
  useEffect(() => { document.body.classList.add("nx-floor-open"); return () => document.body.classList.remove("nx-floor-open"); }, []);

  const hidden = !usdOn || (tv && !d.control?.money);
  const st = useMemo(() => statusOf(evs, now, d.activity?.orders, hidden), [evs, now, d.activity, hidden]);
  const nexusEv = (feed.byAgent.nexus || []).at(-1)?.payload;
  const chief = (d.floor?.log || []).find((l) => l.agent === "chief" && l.response);
  const caption = maskText(firstLine(chief?.response || nexusEv?.text, 400), hidden);
  const open = (d.positions || []).filter((p) => p.status === "open");
  const equity = equityOf(d.account, open);

  if (tv) return <FloorTvDashboard d={d} equity={equity} gates={nexusEv?.gates} hidden={hidden} uid={session.user_id} />;
  return (
    <div className="fc-room h-full flex flex-col overflow-hidden" data-testid="floor-command-room">
      <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden p-3 flex flex-col gap-3">
        <FloorHeader pnl={d.pnl} equity={equity} live={d.live} gates={nexusEv?.gates} hidden={hidden} usdOn={usdOn} onToggleUsd={tv ? null : toggleUsd} />
        <div className="lg:flex-1 lg:min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(300px,27%)] gap-3">
          <div className="flex flex-col gap-2 min-h-0">
            <FloorSlideshow st={st} positions={d.positions} hidden={hidden} />
            {caption && <p className="fc-num text-xs text-cyan-100/85 px-1 leading-relaxed break-words" data-testid="floor-brain-caption"><span className="text-[#00F0FF]">NEXUS ▸</span> {caption}</p>}
          </div>
          <div className="flex flex-col gap-3 min-h-0">
            <PositionCards positions={d.positions} hidden={hidden} equity={equity} className="lg:flex-1" />
            <Vault items={vault} />
            <OrderLog orders={d.activity?.orders} hidden={hidden} />
          </div>
        </div>
      </div>
      <footer className="shrink-0 h-7 border-t border-cyan-300/10 bg-black/60 flex items-center justify-center fc-label !text-[9px]" data-testid="floor-footer">
        POWERED BY <span className="text-[#00F0FF] ml-1.5">A V TECH</span>
      </footer>
    </div>
  );
}
